import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, MapPin, Phone, Package, Wallet, CreditCard, Banknote, Split } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });

export default function KuryeDelivery() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState("cash");
  const [cashAmt, setCashAmt] = useState("");
  const [cardAmt, setCardAmt] = useState("");
  const [transferAmt, setTransferAmt] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/kurye/t/${token}`);
        setOrder(r.data);
        if (r.data.delivery_completed_at) setDone(true);
        setPayment(r.data.payment_method || "cash");
      } catch (e) {
        toast.error("Sipariş bulunamadı");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const total = Number(order?.total || 0);
  const partialSum = useMemo(
    () => Math.round(((+cashAmt || 0) + (+cardAmt || 0) + (+transferAmt || 0)) * 100) / 100,
    [cashAmt, cardAmt, transferAmt]
  );
  const partialDiff = useMemo(() => +(partialSum - total).toFixed(2), [partialSum, total]);
  const partialOk = payment !== "partial" || Math.abs(partialDiff) <= 1.01;

  const submit = async () => {
    const label =
      payment === "cash" ? "NAKİT"
      : payment === "card" ? "KART"
      : payment === "transfer" ? "HAVALE"
      : `KISMİ (Nakit ₺${(+cashAmt || 0).toFixed(2)} + Kart ₺${(+cardAmt || 0).toFixed(2)} + Havale ₺${(+transferAmt || 0).toFixed(2)})`;
    if (!window.confirm(`${label} ile tahsil edildiğini onaylıyor musun?`)) return;
    if (payment === "partial" && !partialOk) {
      toast.error(`Kısmi ödeme toplamı ₺${partialSum.toFixed(2)} — sipariş toplamı ₺${total.toFixed(2)} ile eşleşmiyor`);
      return;
    }
    setSubmitting(true);
    try {
      const body = { payment_method: payment, note: note || null };
      if (payment === "partial") {
        body.cash_amount = +cashAmt || 0;
        body.card_amount = +cardAmt || 0;
        body.transfer_amount = +transferAmt || 0;
      }
      await api.post(`/kurye/t/${token}/complete`, body);
      setDone(true);
      toast.success("Teslimat onaylandı! Admin'e bilgi gitti.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi — tekrar dene");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] text-[#2E5A36]">Yükleniyor...</div>;
  if (!order) return <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] text-red-600">Sipariş bulunamadı</div>;

  const pay = order.payment_collected_method;
  const payLabel = pay === "cash" ? "NAKİT" : pay === "card" ? "KART" : pay === "transfer" ? "HAVALE" : pay === "partial" ? "KISMİ" : pay;

  return (
    <div className="min-h-screen bg-[#FAF7F2] py-6 px-4">
      <div className="max-w-md mx-auto space-y-4">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-[#2E5A36]">🛵 Teslimat</h1>
          <p className="text-sm text-[#1B2A22]/60 font-mono mt-1">Sipariş #{order.order_number}</p>
        </div>

        {done ? (
          <div className="bg-[#2E5A36] text-white rounded-2xl p-6 text-center" data-testid="kurye-done-banner">
            <Check className="w-16 h-16 mx-auto mb-3" />
            <h2 className="font-heading text-xl font-bold">Teslimat Tamamlandı</h2>
            <p className="text-sm mt-2 opacity-90">Admin'e bilgi gönderildi. Pencereyi kapatabilirsin.</p>
            <div className="mt-4 pt-4 border-t border-white/30 text-sm">
              <div>Ödeme: <b>{payLabel}</b></div>
              <div className="font-mono mt-1">₺{order.total.toFixed(2)}</div>
              {pay === "partial" && (
                <div className="mt-2 text-xs opacity-90">
                  Nakit ₺{(order.payment_cash_amount || 0).toFixed(2)} · Kart ₺{(order.payment_card_amount || 0).toFixed(2)} · Havale ₺{(order.payment_transfer_amount || 0).toFixed(2)}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Customer */}
            <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Package className="w-4 h-4 text-[#2E5A36] mt-1" />
                <div className="flex-1">
                  <p className="font-bold text-[#1B2A22]">{order.customer_name}</p>
                  <p className="text-sm text-[#1B2A22]/70">{order.address}</p>
                </div>
              </div>
              <a href={`tel:${order.phone}`} className="flex items-center gap-2 text-[#D9653B] font-bold text-sm" data-testid="kurye-call-btn">
                <Phone className="w-4 h-4" /> {order.phone}
              </a>
              {order.gps_link && (
                <a href={order.gps_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#2E5A36] font-bold text-sm" data-testid="kurye-map-btn">
                  <MapPin className="w-4 h-4" /> Haritada Aç
                </a>
              )}
            </div>

            {/* Items */}
            <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4">
              <p className="text-xs uppercase font-heading font-bold text-[#2E5A36] mb-2">Ürünler</p>
              <ul className="space-y-1 text-sm">
                {(order.items || []).map((i, idx) => (
                  <li key={idx} className="flex justify-between gap-2">
                    <span><span className="font-mono font-bold text-[#2E5A36]">{i.quantity}×</span> {i.name}</span>
                    <span className="font-mono">₺{(i.price * i.quantity).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              {order.notes && (
                <div className="mt-3 pt-3 border-t border-[#E8E2D5]">
                  <p className="text-xs uppercase font-heading font-bold text-[#D9653B]">Müşteri Notu</p>
                  <p className="text-sm mt-1">{order.notes}</p>
                </div>
              )}
            </div>

            {/* Total */}
            <div className="bg-[#2E5A36] text-white rounded-2xl p-4 text-center">
              <p className="text-xs uppercase opacity-80">TAHSİL EDİLECEK</p>
              <p className="font-mono text-3xl font-bold mt-1">₺{order.total.toFixed(2)}</p>
            </div>

            {/* Payment selection */}
            <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4">
              <p className="text-xs uppercase font-heading font-bold text-[#2E5A36] mb-3">Ödeme Yöntemi</p>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { v: "cash", l: "Nakit", icon: Banknote },
                  { v: "card", l: "Kart", icon: CreditCard },
                  { v: "transfer", l: "Havale", icon: Wallet },
                  { v: "partial", l: "Kısmi", icon: Split },
                ].map((o) => {
                  const Icon = o.icon;
                  const active = payment === o.v;
                  return (
                    <button
                      key={o.v}
                      data-testid={`kurye-payment-${o.v}`}
                      onClick={() => setPayment(o.v)}
                      className={`rounded-xl p-2 border-2 transition flex flex-col items-center gap-1 ${active ? "border-[#2E5A36] bg-[#2E5A36]/10" : "border-[#E8E2D5] bg-white"}`}
                    >
                      <Icon className={`w-5 h-5 ${active ? "text-[#2E5A36]" : "text-[#1B2A22]/50"}`} />
                      <span className={`text-xs font-bold ${active ? "text-[#2E5A36]" : "text-[#1B2A22]/70"}`}>{o.l}</span>
                    </button>
                  );
                })}
              </div>

              {/* Partial amounts */}
              {payment === "partial" && (
                <div data-testid="kurye-partial-panel" className="mt-4 space-y-2">
                  <p className="text-[11px] text-[#1B2A22]/70">
                    Toplam ₺{total.toFixed(2)} — kalan tutara göre her hücreye tahsil edilen miktarı yaz. 3 alan toplamı toplama eşit olmalı.
                  </p>
                  {[
                    { k: "cash", l: "Nakit (₺)", val: cashAmt, set: setCashAmt, icon: Banknote },
                    { k: "card", l: "Kart (₺)", val: cardAmt, set: setCardAmt, icon: CreditCard },
                    { k: "transfer", l: "Havale (₺)", val: transferAmt, set: setTransferAmt, icon: Wallet },
                  ].map(({ k, l, val, set, icon: Icon }) => (
                    <div key={k} className="flex items-center gap-2">
                      <div className="w-28 flex items-center gap-1 text-xs font-semibold text-[#1B2A22]/80">
                        <Icon className="w-4 h-4 text-[#2E5A36]" /> {l}
                      </div>
                      <Input
                        data-testid={`kurye-partial-${k}`}
                        inputMode="decimal"
                        type="number"
                        step="any"
                        min="0"
                        placeholder="0.00"
                        value={val}
                        onChange={(e) => set(e.target.value)}
                        className="flex-1 bg-[#FAF7F2] font-mono text-right"
                      />
                    </div>
                  ))}
                  <div className={`flex justify-between items-center rounded-xl p-3 border-2 font-mono text-sm ${partialOk ? "border-[#2E5A36] bg-[#2E5A36]/10 text-[#2E5A36]" : "border-red-400 bg-red-50 text-red-700"}`}>
                    <span className="font-heading font-bold">Girilen Toplam</span>
                    <span data-testid="kurye-partial-sum" className="font-mono font-extrabold">₺{partialSum.toFixed(2)}</span>
                  </div>
                  {!partialOk && (
                    <p data-testid="kurye-partial-error" className="text-xs text-red-600">
                      Fark: {partialDiff > 0 ? "+" : ""}₺{partialDiff.toFixed(2)} — toplama eşitlemek için düzelt.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Note */}
            <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4">
              <p className="text-xs uppercase font-heading font-bold text-[#2E5A36] mb-2">Not (opsiyonel)</p>
              <Textarea
                data-testid="kurye-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Örn: Müşteri evde yoktu, komşuya bıraktım"
                className="bg-white"
                rows={2}
              />
            </div>

            {/* Submit */}
            <Button
              data-testid="kurye-complete-btn"
              onClick={submit}
              disabled={submitting || !partialOk}
              className="w-full h-14 bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading text-lg font-bold disabled:opacity-50"
            >
              {submitting ? "Kaydediliyor..." : "✓ TESLİM EDİLDİ"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
